// Canned search criteria for lead qualification
// This file contains predefined search criteria that can be used for lead generation

export const cannedSearchCriteria = [
  {
    id: "tech-startups-west",
    name: "Tech Startups - West Coast",
    description:
      "Target technology startups on the West Coast with CTO decision makers",
    criteria: {
      companySize: "11-50",
      industry: ["technology"],
      jobTitle: "CTO",
      location: "San Francisco, CA",
      technologies: ["react", "node"],
      budgetRange: "50k-100k",
      timeline: "short-term",
      decisionMaker: true,
    },
    metadata: {
      priority: "high",
      expectedResponseRate: 0.15,
      estimatedValue: 75000,
    },
  },
  {
    id: "finance-nyc",
    name: "Finance Companies - NYC",
    description: "Target finance companies in NYC with CFO decision makers",
    criteria: {
      companySize: "51-200",
      industry: ["finance"],
      jobTitle: "CFO",
      location: "New York, NY",
      technologies: ["python", "aws"],
      budgetRange: "100k+",
      timeline: "immediate",
      decisionMaker: true,
    },
    metadata: {
      priority: "high",
      expectedResponseRate: 0.12,
      estimatedValue: 120000,
    },
  },
  {
    id: "edtech-national",
    name: "EdTech - National",
    description:
      "Target education technology companies nationwide with VP Engineering decision makers",
    criteria: {
      companySize: "201-500",
      industry: ["education", "technology"],
      jobTitle: "VP Engineering",
      location: "USA",
      technologies: ["react", "gcp"],
      budgetRange: "50k-100k",
      timeline: "short-term",
      decisionMaker: true,
    },
    metadata: {
      priority: "medium",
      expectedResponseRate: 0.1,
      estimatedValue: 85000,
    },
  },
  {
    id: "healthcare-midwest",
    name: "Healthcare - Midwest",
    description:
      "Target healthcare companies in the Midwest with IT Director decision makers",
    criteria: {
      companySize: "501-1000",
      industry: ["healthcare"],
      jobTitle: "IT Director",
      location: "Chicago, IL",
      technologies: ["aws", "azure"],
      budgetRange: "100k+",
      timeline: "long-term",
      decisionMaker: true,
    },
    metadata: {
      priority: "medium",
      expectedResponseRate: 0.08,
      estimatedValue: 95000,
    },
  },
  {
    id: "saas-startups-remote",
    name: "SaaS Startups - Remote First",
    description:
      "Target remote-first SaaS startups with VP Engineering decision makers",
    criteria: {
      companySize: "11-50",
      industry: ["technology", "software"],
      jobTitle: "VP Engineering",
      location: "Remote",
      technologies: ["react", "node", "aws"],
      budgetRange: "50k-100k",
      timeline: "short-term",
      decisionMaker: true,
    },
    metadata: {
      priority: "high",
      expectedResponseRate: 0.18,
      estimatedValue: 80000,
    },
  },
];

// Helper function to enhance queue record input with additional metadata
export const enhanceQueueRecordInput = (
  baseInput,
  startupName,
  userMode = "default"
) => {
  const enhancedInput = {
    ...baseInput,
    // Add tenant/entity information
    tenantId: startupName || "default",
    entityType: "LGAPOLLOMGR", // Updated entity type
    userMode,

    // Add timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),

    // Add processing metadata
    priority: baseInput.metadata?.priority || "medium",
    expectedProcessingTime: "5-10 minutes",

    // Add source tracking
    source: "sales-process-dashboard",
    sourceScreen: "lead-search-queue",

    // Add business context
    businessContext: {
      startupName,
      userMode,
      searchType: "apollo-lead-search",
      expectedOutcome: "qualified-lead-list",
      entityType: "LGAPOLLOMGR", // Updated entity type
    },
  };

  return enhancedInput;
};

// Helper function to get canned criteria by ID
export const getCannedCriteriaById = (id) => {
  return cannedSearchCriteria.find((criteria) => criteria.id === id);
};

// Helper function to validate canned criteria
export const validateCannedCriteria = (criteria) => {
  const requiredFields = ["companySize", "industry", "jobTitle"];
  const missingFields = requiredFields.filter((field) => !criteria[field]);

  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
  }

  return true;
};
